/**
 * 飞机备降常量
 *
 * 依据设计文档 `7.0 飞机备降与衍生航段设计.md` §3.5（diversion_reason 存储与交互分离）。
 * 后端 `diversion_reason` 为自由文本字段，前端通过分类标识符前缀拼接为 `[代码] 描述` 格式，
 * 兼顾交互友好与查询统计（统计时按 `[CODE]%` 前缀聚合）。
 *
 * 分类代码对齐 IATA 备降分类，前端可自由增删，不影响后端存储。
 */

/**
 * 备降分类代码（§3.5 IATA 分类表）
 * @property {string} code  存储标识符前缀（方括号包裹后拼入 diversion_reason）
 * @property {string} label 前端下拉显示文本
 * @property {string} group 归类：POSITION=按位置分类，CAUSE=按原因分类
 */
export const DIVERSION_CATEGORIES = [
    { code: "AIR_TURNBACK", label: "起飞后返航 (Air Turnback)", group: "POSITION" },
    { code: "EN_ROUTE", label: "航路备降 (En-route Diversion)", group: "POSITION" },
    { code: "DESTINATION", label: "目的地改降 (Destination Diversion)", group: "POSITION" },
    { code: "WEATHER", label: "天气原因 (Weather)", group: "CAUSE" },
    { code: "MECHANICAL", label: "机械故障 (Mechanical)", group: "CAUSE" },
    { code: "MEDICAL", label: "医疗紧急 (Medical)", group: "CAUSE" },
    { code: "SECURITY", label: "安全威胁 (Security)", group: "CAUSE" },
    { code: "OTHER", label: "其他 (Other)", group: "CAUSE" },
];

/**
 * 分类代码 → 配置映射，便于根据 code 反查。
 */
export const DIVERSION_CATEGORY_BY_CODE = DIVERSION_CATEGORIES.reduce((acc, item) => {
    acc[item.code] = item;
    return acc;
}, {});

/**
 * 衍生航段类型（§3.2 拓扑，对齐数据模型 derivation_type）
 */
export const DERIVATION_TYPE = {
    DIVERSION_LEG: "DIVERSION_LEG", // 备降航段 a-c
    RECOVERY_LEG: "RECOVERY_LEG", // 续飞航段 c-b
};

export const DERIVATION_TYPE_LABELS = {
    [DERIVATION_TYPE.DIVERSION_LEG]: "备降航段",
    [DERIVATION_TYPE.RECOVERY_LEG]: "续飞航段",
};

/**
 * 备降专用变更命令类型（§8.2，不沿用 UPDATE_FLIGHT_INFO）
 */
export const CHANGE_TYPE_PROCESS_DIVERSION = "PROCESS_DIVERSION";

/**
 * 将分类代码与自由描述拼接为 `diversion_reason` 存储格式（§3.5）。
 *
 * 规则：
 * - 有分类代码：`[CODE] 描述`
 * - 无分类代码（用户选择"自定义/不加分类"）：描述原样返回
 * - 两者皆空：返回空字符串
 *
 * @param {string|null|undefined} categoryCode 分类代码，见 DIVERSION_CATEGORIES
 * @param {string} description 自由描述文本
 * @returns {string} 拼接后的 diversion_reason 字符串
 */
export const buildDiversionReason = (categoryCode, description) => {
    const trimmedDesc = (description || "").trim();
    if (!categoryCode && !trimmedDesc) return "";
    if (!categoryCode) return trimmedDesc;
    if (!trimmedDesc) return `[${categoryCode}]`;
    return `[${categoryCode}] ${trimmedDesc}`;
};

/**
 * 从已存储的 diversion_reason 文本反向解析分类代码与描述（用于详情展示）。
 *
 * @param {string} reasonText 原始 diversion_reason
 * @returns {{ code: string|null, description: string }}
 */
export const parseDiversionReason = (reasonText) => {
    const text = (reasonText || "").trim();
    if (!text) return { code: null, description: "" };
    const match = text.match(/^\[([A-Z_]+)\]\s?(.*)$/);
    if (!match) return { code: null, description: text };
    const [, code, description] = match;
    return { code: DIVERSION_CATEGORY_BY_CODE[code] ? code : null, description: (description || "").trim() };
};
